import type { CatalogEvent, Preview } from "@livcg/schema";

export type Cue = Omit<Preview, "setAt">;
export type Side = "home" | "away";
export type CuePlayer = { id: string; firstName: string; lastName: string; number: number | null };
export type GoalType = "goal" | "penalty_goal" | "own_goal";
export type CardType = "yellow_card" | "second_yellow" | "red_card";

const other = (side: Side): Side => (side === "home" ? "away" : "home");
const surname = (p: CuePlayer) => (p.lastName || p.firstName).trim();
const tagOf = (p: CuePlayer) => `${p.number != null ? `#${p.number} ` : ""}${surname(p)}`;

const GOAL_LABEL: Record<GoalType, string> = {
  goal: "GOL",
  penalty_goal: "PENALTI GOL",
  own_goal: "AVTOGOL",
};

/** A goal shows its first banner and the new score; an own goal scores and
 *  shows for the other side (as the event chain does). */
export function goalCue(type: GoalType, side: Side, player: CuePlayer): Cue {
  const scoring = type === "own_goal" ? other(side) : side;
  return {
    mode: `little_event_goal_${scoring}`,
    data: {},
    scoreDelta: { home: scoring === "home" ? 1 : 0, away: scoring === "away" ? 1 : 0 },
    label: `${GOAL_LABEL[type]} · ${tagOf(player)}`,
    action: { kind: "event", type, side, playerId: player.id },
  };
}

const CARD: Record<CardType, { kind: string; label: string }> = {
  yellow_card: { kind: "yellow", label: "SARIQ" },
  second_yellow: { kind: "secondyellow", label: "2-SARIQ → QIZIL" },
  red_card: { kind: "red", label: "QIZIL" },
};

export function cardCue(type: CardType, side: Side, player: CuePlayer, isCoach = false): Cue {
  return {
    mode: `little_card_${CARD[type].kind}_${side}`,
    data: { playerNumber: player.number ?? (isCoach ? "" : 0), playerName: surname(player) },
    label: `${CARD[type].label} · ${tagOf(player)}`,
    action: { kind: "event", type, side, playerId: player.id },
  };
}

/** A yellow for an already booked player is the second yellow. */
export function yellowType(yellows: number): CardType {
  return yellows >= 1 ? "second_yellow" : "yellow_card";
}

export type SubPair = { out: CuePlayer; in: CuePlayer };

export function subsCue(side: Side, pairs: SubPair[]): Cue {
  return {
    mode: `little_card_swap_${side}`,
    data: {
      swaps: pairs.map((p) => ({
        outNumber: p.out.number ?? 0,
        outName: surname(p.out),
        inNumber: p.in.number ?? 0,
        inName: surname(p.in),
      })),
    },
    label: `ALMASHTIRISH · ${pairs.map((p) => `${surname(p.out)} → ${surname(p.in)}`).join(", ")}`,
    action: { kind: "subs", side, pairs: pairs.map((p) => ({ outId: p.out.id, inId: p.in.id })) },
  };
}

const BANNER: [RegExp, string][] = [
  [/^little_event_goal_/, "GOL banneri"],
  [/^little_event_scorer_/, "Gol muallifi"],
  [/^little_card_secondyellow_/, "2-sariq banneri"],
  [/^little_card_yellowcancel_/, "Sariq bekor qilindi"],
  [/^little_card_yellow_/, "Sariq banneri"],
  [/^little_card_red_/, "Qizil banneri"],
  [/^little_card_swap_/, "Almashtirish banneri"],
];

/** A readable name for the chain banners the overlay catalog does not list. */
export function bannerLabel(mode: string): string | null {
  return BANNER.find(([re]) => re.test(mode))?.[1] ?? null;
}

export function showCue(mode: string, data: unknown, label: string): Cue {
  return { mode, data, label, action: { kind: "show", mode } };
}

export function hideCue(
  mode: string,
  target: { mode: string | null; data: unknown },
  label: string,
): Cue {
  return { mode: target.mode, data: target.data, label, action: { kind: "hide", mode } };
}

/** Who is on the pitch now: starters, minus those subbed off, plus those
 *  subbed on (the log order is the match order). */
export function onPitch(
  starterIds: readonly string[],
  events: readonly CatalogEvent[],
): Set<string> {
  const ids = new Set(starterIds);
  for (const e of events) {
    if (e.type !== "substitution") continue;
    ids.delete(e.playerId);
    if (e.assistPlayerId) ids.add(e.assistPlayerId);
  }
  return ids;
}
